<template>
  <div class="col-md-12">
    <div class="col-md-12">
      <a-divider
        >{{ $t('etatPaiement.selectionnerFrais') }}
        <a-button @click="toggleAll" size="small" class="toggle-all-btn">
          <a-icon :type="allCollapsed ? 'plus-square' : 'minus-square'" />
          {{ allCollapsed ? $t('etatPaiement.toutAfficher') : $t('etatPaiement.toutMasquer') }}
        </a-button>
      </a-divider>
      <!-- Avance Switch and Input -->
      <div class="card mb-3">
        <div class="card-body">
          <div class="row align-items-center">
            <div class="col-md-5">
              <div class="d-flex align-items-center">
                <a-switch
                  v-model="payWithAvance"
                  :checked-children="$t('etatPaiement.payerAvecAvance')"
                  :un-checked-children="$t('etatPaiement.sansAvance')"
                  class="mr-3"
                />
                <span class="text-muted small">
                  <a-icon type="info-circle" class="mr-1" />
                  {{ $t('etatPaiement.activerPaiementAvance') }}
                </span>
              </div>
            </div>
            <div class="col-md-7" v-if="payWithAvance">
              <div class="form-group mb-0">
                <label class="form-label small text-muted mb-1">
                  <a-icon type="dollar" class="mr-1" />
                  {{ $t('etatPaiement.montantAvance') }}
                </label>
                <a-input-number
                  v-model="avanceAmount"
                  :min="0"
                  :step="1"
                  :placeholder="$t('etatPaiement.saisirMontant')"
                  class="w-100"
                  size="large"
                />
              </div>
            </div>
            <div class="col-md-12" v-if="payWithAvance">
              <a-alert
                v-if="maxAvanceAmount < avanceAmount"
                class="mt-2"
                type="error"
                banner
                :message="`${$t('etatPaiement.montantSupTotal')} ${maxAvanceAmount.toFixed(2)}`"
              />
              <a-alert
                v-if="invalidAvance"
                class="mt-2"
                type="error"
                banner
                :message="$t('etatPaiement.montantInsuffisant')"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Payment Cards Section -->
    <div class="col-md-12 payment-cards-section">
      <div class="payment-cards-container">
        <div
          class="payment-card-wrapper"
          v-for="(payment, index) in allDataPayment"
          :key="payment._id"
        >
          <a-card
            class="payment-card"
            :class="{
              'payment-card-selected': hasSelectedFees(payment._id),
            }"
          >
            <template slot="title">
              <h4>
                {{ payment.name }}
                <a-tag
                  v-show="payment.student"
                  color="#108ee9"
                  class="student-name-tag"
                  >{{ payment.student.fullName }}</a-tag
                >
                <a-tag color="blue" v-show="payment.montantTotal">{{
                  payment.montantTotal.toFixed(3) + " " + currency
                }}</a-tag>
              </h4>
            </template>
            <template slot="extra">
              <a-tag :color="payment.type === 'classRoom' ? 'blue' : 'orange'">
                {{ payment.type === "classRoom" ? $t('etatPaiement.scolarite') : $t('etatPaiement.activite') }}
              </a-tag>
              <a-button
                shape="circle"
                size="small"
                @click.stop="toggleCollapse(payment._id)"
              >
                <a-icon :type="collapsedState[payment._id] ? 'down' : 'up'" />
              </a-button>
            </template>

            <div v-if="!collapsedState[payment._id]">
              <DailyPaymentAttendanceIndication
                v-if="payment.daily && resolveStudentId(payment) && resolveActivityId(payment)"
                :student-id="resolveStudentId(payment)"
                :activity-id="resolveActivityId(payment)"
              />

              <!-- Fees Table -->
              <div class="fees-section">
                <a-divider>
                  {{ $t('etatPaiement.fraisAPayer') }}
                  <span v-if="payment.daily" class="daily-fees-info">
                    ({{ getUnpaidFeesCount(payment) }} {{ $t('etatPaiement.fraisNonPayes') }})
                  </span>
                </a-divider>
                
                <!-- Daily Activities - Compact Grid View -->
                <div v-if="payment.daily" class="daily-fees-container">
                  <div class="daily-fees-grid">
                    <div
                      v-for="(fee, index) in getPaginatedFeeData(payment)"
                      :key="`${payment._id}_${fee.feeName}_${index}`"
                      class="daily-fee-item"
                      :class="{
                        'amount-selected': checked[payment._id][fee.feeName],
                        'amount-with-remaining': fee.remaining !== null && fee.remaining !== undefined,
                        'fee-paid': fee.fullyPaid
                      }"
                      @click="handleFeeSelection(payment._id, fee.feeName, !checked[payment._id][fee.feeName])"
                    >
                      <div class="fee-date">
                        {{ formatFeeName(fee.feeName, payment.daily) }}
                      </div>
                      
                      <div class="fee-amount">
                        {{ fee.feeAfterReduction.toFixed(2) }} {{ currency }}
                        <a-tag
                          v-if="onTransReduction > 0 && anyFeeSelected"
                          color="green"
                          size="small"
                        >
                          -{{ onTransReduction.toFixed(2) }}%
                        </a-tag>
                      </div>
                      
                      <div
                        v-if="onTransReduction > 0 && anyFeeSelected"
                        class="fee-final-amount"
                      >
                        = {{ getFeeAmountAfterReduction(fee).toFixed(2) }} {{ currency }}
                      </div>
                      
                      <div
                        class="fee-amount"
                        v-if="!checked[payment._id][fee.feeName] && fee.remaining"
                      >
                        {{ $t('etatPaiement.reste') }}
                      </div>
                      
                      <!-- Avance Distribution Display -->
                      <div
                        v-if="payWithAvance && checked[payment._id][fee.feeName] && avanceAmount > 0"
                        class="fee-final-amount"
                        style="color: #faad14"
                      >
                        {{ $t('etatPaiement.avanceAppliquee') }} : {{ getAvanceForFee(payment._id, fee.feeName).toFixed(2) }} {{ currency }}
                        
                        <!-- Remaining Amount Display -->
                        <div
                          v-if="isPartialPayment(payment._id, fee.feeName, fee)"
                          class="fee-remaining-amount"
                          style="color: #ff4d4f; font-size: 11px; margin-top: 2px;"
                        >
                          {{ $t('etatPaiement.restePayer') }} : {{ (getFeeAmountAfterReduction(fee) - getAvanceForFee(payment._id, fee.feeName)).toFixed(2) }} {{ currency }}
                        </div>
                      </div>
                      
                      <div class="selection-indicator">
                        <a-icon
                          v-if="isPartialPayment(payment._id, fee.feeName, fee)"
                          type="exclamation-circle"
                          theme="filled"
                          style="color: #fa8c16; margin-left: 8px; font-size: 14px;"
                          :title="$t('etatPaiement.paiementPartiel')"
                        />
                        <a-icon
                          v-else-if="isInvalidAvanceApplied(payment._id, fee.feeName)"
                          type="exclamation-circle"
                          theme="filled"
                          style="color: #ff4d4f; margin-left: 8px; font-size: 14px;"
                          :title="$t('etatPaiement.montantInvalide')"
                        />
                        <a-icon
                          v-else
                          :type="checked[payment._id][fee.feeName] ? 'check-circle' : 'circle'"
                          :theme="checked[payment._id][fee.feeName] ? 'filled' : 'outlined'"
                          :style="{
                            color: checked[payment._id][fee.feeName]
                              ? fee.remaining !== null && fee.remaining !== undefined
                                ? '#faad14'
                                : '#52c41a'
                              : '#d9d9d9',
                          }"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <!-- Pagination for Daily Fees -->
                  <div v-if="getTotalPages(payment) > 1" class="daily-fees-pagination">
                    <a-pagination
                      :current="dailyFeesPagination[payment._id] || 1"
                      :total="getUnpaidFeesCount(payment)"
                      :page-size="dailyFeesPerPage"
                      :show-size-changer="false"
                      :show-quick-jumper="false"
                      size="small"
                      @change="(page) => setDailyFeesPage(payment._id, page)"
                    />
                    <div class="pagination-info">
                      {{ $t('all.pagination.page') }} {{ dailyFeesPagination[payment._id] || 1 }} {{ $t('all.sur') || 'of' }} {{ getTotalPages(payment) }}
                    </div>
                  </div>
                </div>
                
                <!-- Regular Table for Non-Daily Activities -->
                <a-table
                  v-else
                  :columns="getFeeColumns()"
                  :data-source="getFeeData(payment)"
                  :key="payment._id"
                  :pagination="false"
                  size="small"
                >
                  <template slot="feeName" slot-scope="text, record">
                    <div class="fee-name">
                      <a-icon
                        :type="
                          record.feeName === 'inscription'
                            ? 'user-add'
                            : 'calendar'
                        "
                        class="mr-2"
                      />
                      {{ getFeeLabel(record.feeName, payment.mode) }}
                    </div>
                  </template>

                  <template slot="amount" slot-scope="text, record">
                    <div
                      v-if="
                        record.fullyPaid === false || record.fullyPaid === null
                      "
                      class="clickable-amount"
                      :class="{
                        'amount-selected': checked[payment._id][record.feeName],
                        'amount-with-remaining':
                          record.remaining !== null &&
                          record.remaining !== undefined,
                      }"
                      @click="
                        handleFeeSelection(
                          payment._id,
                          record.feeName,
                          !checked[payment._id][record.feeName]
                        )
                      "
                    >
                      <div class="fee-amount">
                        {{ record.feeAfterReduction.toFixed(2) }}
                        {{ currency }}
                        <a-tag
                          v-if="onTransReduction > 0 && anyFeeSelected"
                          color="green"
                          size="small"
                        >
                          -{{ onTransReduction.toFixed(2) }}%
                        </a-tag>
                      </div>
                      <div
                        v-if="onTransReduction > 0 && anyFeeSelected"
                        class="fee-final-amount"
                      >
                        =
                        {{ getFeeAmountAfterReduction(record).toFixed(2) }}
                        {{ currency }}
                      </div>
                      <div
                        class="fee-amount"
                        v-if="
                          !checked[payment._id][record.feeName] &&
                          record.remaining
                        "
                      >
                        {{ $t("etatPaiement.reste") }}
                      </div>
                      <!-- Avance Distribution Display -->
                      <div
                        v-if="
                          payWithAvance &&
                          checked[payment._id][record.feeName] &&
                          avanceAmount > 0
                        "
                        class="fee-final-amount"
                        style="color: #faad14"
                      >
                        {{ $t("etatPaiement.avanceAppliquee") }}:
                        {{
                          getAvanceForFee(payment._id, record.feeName).toFixed(
                            2
                          )
                        }}
                        {{ currency }}

                        <!-- Remaining Amount Display -->
                        <div
                          v-if="
                            isPartialPayment(
                              payment._id,
                              record.feeName,
                              record
                            )
                          "
                          class="fee-remaining-amount"
                          style="
                            color: #ff4d4f;
                            font-size: 11px;
                            margin-top: 2px;
                          "
                        >
                          {{ $t("etatPaiement.restePayer") }}:
                          {{
                            (
                              getFeeAmountAfterReduction(record) -
                              getAvanceForFee(payment._id, record.feeName)
                            ).toFixed(2)
                          }}
                          {{ currency }}
                        </div>
                      </div>
                      <div class="selection-indicator">
                        <a-icon
                          v-if="
                            isPartialPayment(
                              payment._id,
                              record.feeName,
                              record
                            )
                          "
                          type="exclamation-circle"
                          theme="filled"
                          style="
                            color: #fa8c16;
                            margin-left: 8px;
                            font-size: 14px;
                          "
                          :title="$t('etatPaiement.paiementPartiel')"
                        />
                        <a-icon
                          v-else-if="
                            isInvalidAvanceApplied(payment._id, record.feeName)
                          "
                          type="exclamation-circle"
                          theme="filled"
                          style="
                            color: #ff4d4f;
                            margin-left: 8px;
                            font-size: 14px;
                          "
                          :title="$t('etatPaiement.montantInvalide')"
                        />
                        <a-icon
                          v-else
                          :type="
                            checked[payment._id][record.feeName]
                              ? 'check-circle'
                              : 'circle'
                          "
                          :theme="
                            checked[payment._id][record.feeName]
                              ? 'filled'
                              : 'outlined'
                          "
                          :style="{
                            color: checked[payment._id][record.feeName]
                              ? record.remaining !== null &&
                                record.remaining !== undefined
                                ? '#faad14'
                                : '#52c41a'
                              : '#d9d9d9',
                          }"
                        />
                      </div>
                    </div>
                    <div v-else class="text-muted">
                      <a-icon
                        type="check-circle"
                        theme="filled"
                        class="text-success"
                      />
                      {{ $t('profil.payes') }}
                    </div>
                  </template>
                </a-table>
              </div>

              <!-- Card Actions -->
              <div class="card-actions">
                <a-button
                  type="primary"
                  @click="handleToggleAllFees(payment._id)"
                  class="rounded-pill"
                >
                  <a-icon
                    :type="
                      areAllFeesSelected(payment._id)
                        ? 'minus-square'
                        : 'plus-square'
                    "
                  />
                  {{
                    areAllFeesSelected(payment._id)
                      ? $t('etatPaiement.deselecTout')
                      : $t('etatPaiement.selectionnerTout')
                  }}
                </a-button>
              </div>
            </div>
          </a-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { formatFeeName } from "@/utilities/settings";
import { getBuildingCurrency } from "@/utilities/countrySpecific";
import DailyPaymentAttendanceIndication from "./DailyPaymentAttendanceIndication.vue";

export default {
  name: "PaymentCardsSection",
  components: {
    DailyPaymentAttendanceIndication,
  },

  data() {
    return {
      collapsedState: {},
      allCollapsed: false,
      payWithAvance: false,
      avanceAmount: 0,
      invalidAvance: false,
      dailyFeesPagination: {},
      dailyFeesPerPage: 12, // Show 12 daily fees per page
      currency: null,
    };
  },
  
  created() {
    this.currency = getBuildingCurrency();
  },

  computed: {
    maxAvanceAmount() {
      // Maximum avance is the sum of all selected fees
      let total = 0;
      this.allDataPayment.forEach((payment) => {
        if (!payment.fees) return;
        payment.fees.forEach((fee) => {
          if (
            this.checked[payment._id] &&
            this.checked[payment._id][fee.feeName]
          ) {
            total += this.getFeeAmountAfterReduction(fee);
          }
        });
      });
      return total;
    },
    avanceDistribution() {
      // Returns an object: { [paymentId]: { [feeName]: amountPaidFromAvance } }
      let remainingAvance =
        this.avanceAmount -
        (this.selectedTypeFR == "F" ? this.fiscalTimbre : 0);

      // First, collect all selected fees with their amounts and sort by amount (lowest first)
      const selectedFees = [];
      this.allDataPayment.forEach((payment) => {
        if (!payment.fees) return;
        payment.fees.forEach((fee) => {
          if (
            this.checked[payment._id] &&
            this.checked[payment._id][fee.feeName]
          ) {
            const feeAmount = this.getFeeAmountAfterReduction(fee);
            selectedFees.push({
              paymentId: payment._id,
              feeName: fee.feeName,
              amount: feeAmount,
              originalFee: fee,
            });
          }
        });
      });

      // Sort fees by amount (lowest first) to prioritize paying smaller fees completely
      selectedFees.sort((a, b) => a.amount - b.amount);

      // Initialize result object
      const result = {};
      this.allDataPayment.forEach((payment) => {
        if (!payment.fees) return;
        if (!result[payment._id]) result[payment._id] = {};
        payment.fees.forEach((fee) => {
          result[payment._id][fee.feeName] = 0;
        });
      });

      // Distribute avance amount starting with the lowest fees
      for (const feeInfo of selectedFees) {
        if (remainingAvance <= 0) break;

        const toPay = Math.min(feeInfo.amount, remainingAvance);
        result[feeInfo.paymentId][feeInfo.feeName] = toPay;
        remainingAvance -= toPay;
      }

      return result;
    },
    anyFeeSelected() {
      // Returns true if any fee is selected in any payment
      for (const payment of this.allDataPayment) {
        if (!payment.fees) continue;
        for (const fee of payment.fees) {
          if (
            this.checked[payment._id] &&
            this.checked[payment._id][fee.feeName]
          ) {
            return true;
          }
        }
      }
      return false;
    },
  },

  watch: {
    allDataPayment: {
      handler(newVal) {
        if (newVal) {
          newVal.forEach((payment) => {
            if (this.collapsedState[payment._id] === undefined) {
              this.$set(this.collapsedState, payment._id, false);
            }
          });
          this.checkAllCollapsedState();
        }
      },
      immediate: true,
      deep: true,
    },
    payWithAvance: {
      handler(newVal) {
        if (!newVal) {
          // Reset avance amount and distribution when payWithAvance is turned off
          this.avanceAmount = 0;
          this.invalidAvance = false;
          this.$emit("update-avance-distribution", {});
          this.$emit("update-invalid-avance", false);
        } else {
          // Emit current distribution when payWithAvance is turned on
          this.invalidAvance = this.checkInvalidAvanceForSelectedFees();

          this.emitAvanceDistribution();
          this.$emit(
            "update-invalid-avance",
            this.invalidAvance || this.maxAvanceAmount < this.avanceAmount
          );
        }
      },
    },
    avanceAmount: {
      handler() {
        this.emitAvanceDistribution();
        this.invalidAvance = this.checkInvalidAvanceForSelectedFees();
        this.$emit(
          "update-invalid-avance",
          this.invalidAvance || this.maxAvanceAmount < this.avanceAmount
        );
      },
    },
    checked: {
      handler() {
        this.emitAvanceDistribution();
        this.invalidAvance = this.checkInvalidAvanceForSelectedFees();
        this.$emit(
          "update-invalid-avance",
          this.invalidAvance || this.maxAvanceAmount < this.avanceAmount
        );
      },
      deep: true,
    },
    onTransReduction: {
      handler() {
        this.emitAvanceDistribution();
      },
    },
  },

  props: {
    allDataPayment: {
      required: true,
      type: Array,
    },
    checked: {
      required: true,
      type: Object,
    },
    selectedTypeFR: {
      required: true,
      type: String,
    },
    onTransReduction: {
      required: true,
      type: Number,
    },
    fiscalTimbre: {
      type: Number,
      default: () => parseFloat(process.env.VUE_APP_FISCAL) || 0,
    },
  },

  methods: {
    formatFeeName,
    toggleAll() {
      this.allCollapsed = !this.allCollapsed;
      Object.keys(this.collapsedState).forEach((paymentId) => {
        this.$set(this.collapsedState, paymentId, this.allCollapsed);
      });
    },

    checkAllCollapsedState() {
      const states = Object.values(this.collapsedState);
      if (states.length > 0 && states.every((s) => s)) {
        this.allCollapsed = true;
      } else if (states.length > 0 && states.every((s) => !s)) {
        this.allCollapsed = false;
      }
    },

    toggleCollapse(paymentId) {
      this.$set(
        this.collapsedState,
        paymentId,
        !this.collapsedState[paymentId]
      );
      this.checkAllCollapsedState();
    },

    getFeeLabel(key, mode) {
      try {
        if (key == "inscription") return this.$t('etatPaiement.inscription');
        if (mode == "M")
          return (
            moment(key).format("MMMM") +
            " (" +
            moment(key).format("DD/MM/YYYY") +
            ")"
          );
        if (mode == "T") return this.$t('etatPaiement.tranche') + " " + key;
      } catch (error) {
        console.error(error.stack);
        return "--";
      }
    },

    getFeeColumns() {
      return [
        {
          title: this.$t('etatPaiement.frais'),
          dataIndex: "feeName",
          key: "feeName",
          scopedSlots: { customRender: "feeName" },
          width: "45%",
        },
        {
          title: this.$t('etatPaiement.montant'),
          dataIndex: "amount",
          key: "amount",
          scopedSlots: { customRender: "amount" },
          width: "55%",
        },
      ];
    },

    getFeeData(payment) {
      if (!payment.fees) return [];
      return payment.fees.map((fee, index) => ({
        key: `${payment._id}_${fee.feeName}_${index}`,
        feeName: fee.feeName,
        feeAfterReduction: Number(fee.feeAfterReduction),
        fullyPaid: fee.fullyPaid,
        paid: fee.paid,
        remaining: fee.remaining,
      }));
    },

    resolveStudentId(payment) {
      return String(
        payment?.student?._id || payment?.student || ""
      );
    },

    resolveActivityId(payment) {
      return String(
        payment?.activity?._id || payment?.activity || payment?.activityId || ""
      );
    },

    areAllFeesSelected(paymentId) {
      const payment = this.allDataPayment.find((p) => p._id === paymentId);
      if (!payment || !payment.fees) return false;

      const unpaidFees = payment.fees.filter(
        (fee) => fee.fullyPaid === false || fee.fullyPaid === null
      );
      if (unpaidFees.length === 0) return false;

      return unpaidFees.every((fee) => this.checked[paymentId][fee.feeName]);
    },

    hasSelectedFees(paymentId) {
      const payment = this.allDataPayment.find((p) => p._id === paymentId);
      if (!payment || !payment.fees) return false;

      const unpaidFees = payment.fees.filter((fee) => fee.fullyPaid === false);
      return unpaidFees.some((fee) => this.checked[paymentId][fee.feeName]);
    },

    handleFeeSelection(paymentId, feeName, checked) {
      this.$emit("update-fee-selection", paymentId, feeName, checked);
    },

    handleToggleAllFees(paymentId) {
      this.$emit("toggle-all-fees", paymentId);
    },

    getFeeAmountAfterReduction(fee) {
      const baseAmount = Number(fee.feeAfterReduction);
      return this.onTransReduction > 0
        ? baseAmount * ((100 - this.onTransReduction) / 100)
        : baseAmount;
    },

    isPartialPayment(paymentId, feeName, record) {
      if (
        !this.payWithAvance ||
        !this.checked[paymentId] ||
        !this.checked[paymentId][feeName] ||
        this.avanceAmount <= 0
      ) {
        return false;
      }

      const avanceApplied = this.getAvanceForFee(paymentId, feeName);
      if (avanceApplied <= 0) {
        return false;
      }

      const feeAmountAfterReduction = this.getFeeAmountAfterReduction(record);
      return avanceApplied < feeAmountAfterReduction;
    },

    getAvanceForFee(paymentId, feeName) {
      if (!this.payWithAvance || !this.avanceDistribution[paymentId]) return 0;
      return this.avanceDistribution[paymentId][feeName] || 0;
    },
    isInvalidAvanceApplied(paymentId, feeName) {
      if (!this.payWithAvance || !this.avanceDistribution[paymentId])
        return false;
      return this.avanceDistribution[paymentId][feeName] === 0;
    },
    emitAvanceDistribution() {
      this.$emit(
        "update-avance-distribution",
        this.avanceDistribution,
        this.payWithAvance,
        this.avanceAmount
      );
    },
    checkInvalidAvanceForSelectedFees() {
      if (!this.payWithAvance) return false;
      // Returns true if any selected fee has getAvanceForFee == 0
      for (const payment of this.allDataPayment) {
        if (!payment.fees) continue;
        for (const fee of payment.fees) {
          if (
            this.checked[payment._id] &&
            this.checked[payment._id][fee.feeName] &&
            this.getAvanceForFee(payment._id, fee.feeName) == 0
          ) {
            return true;
          }
        }
      }
      return false;
    },
    
    // Daily fees pagination methods
    getUnpaidFeesCount(payment) {
      if (!payment.fees) return 0;
      return payment.fees.filter(fee => fee.fullyPaid === false || fee.fullyPaid === null).length;
    },
    
    getPaginatedFeeData(payment) {
      if (!payment.fees) return [];
      const unpaidFees = payment.fees.filter(fee => fee.fullyPaid === false || fee.fullyPaid === null);
      const currentPage = this.dailyFeesPagination[payment._id] || 1;
      const startIndex = (currentPage - 1) * this.dailyFeesPerPage;
      const endIndex = startIndex + this.dailyFeesPerPage;
      return unpaidFees.slice(startIndex, endIndex).map((fee, index) => ({
        key: `${payment._id}_${fee.feeName}_${index}`,
        feeName: fee.feeName,
        feeAfterReduction: Number(fee.feeAfterReduction),
        fullyPaid: fee.fullyPaid,
        paid: fee.paid,
        remaining: fee.remaining,
      }));
    },
    
    getTotalPages(payment) {
      const unpaidCount = this.getUnpaidFeesCount(payment);
      return Math.ceil(unpaidCount / this.dailyFeesPerPage);
    },
    
    setDailyFeesPage(paymentId, page) {
      this.$set(this.dailyFeesPagination, paymentId, page);
    },
    
    formatDailyFeeDate(feeName) {
      try {
        return moment(feeName).format('DD/MM');
      } catch (error) {
        return feeName;
      }
    },
  },
};
</script>

<style scoped>
/* Payment Cards Styles */
.payment-cards-section {
  margin-top: 20px;
}

.payment-cards-container {
  column-count: 2;
  column-gap: 20px;
}

.payment-card-wrapper {
  width: 100%;
  break-inside: avoid;
  margin-bottom: 20px;
  display: inline-block;
  vertical-align: top;
}

.payment-card {
  width: 100%;
  transition: all 0.3s ease;
  border: 1px solid #f0f0f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  height: auto;
  min-height: auto;
}

.payment-card-selected {
  border-color: #1890ff;
  box-shadow: 0 4px 12px rgba(24, 144, 255, 0.15);
  animation: pulse 2s infinite;
}

.payment-card .ant-card-head {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-bottom: none;
}

.payment-card .ant-card-head-title {
  color: white;
  font-weight: 600;
}

.payment-card .ant-card-extra {
  color: white;
}

.payment-card .ant-card-body {
  height: auto;
  min-height: auto;
}

.payment-info {
  background: #fafafa;
  padding: 12px;
  border-radius: 8px;
  border: none;
  color: white;
  border-radius: 6px;
  border-left: 4px solid #1890ff;
}

.payment-info small {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.fees-table {
  background: white;
  border-radius: 8px;
  overflow: hidden;
}

.fees-table .ant-table-thead > tr > th {
  background: #f8f9fa;
  border-bottom: 2px solid #e9ecef;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 12px;
  letter-spacing: 0.5px;
}

.fees-table .ant-table-tbody > tr > td {
  border-bottom: 1px solid #f0f0f0;
  padding: 12px 8px;
  vertical-align: top;
}

.fees-table .ant-table-tbody > tr:hover {
  background-color: transparent;
}

.fee-name {
  display: flex;
  align-items: center;
  font-weight: 500;
}

.payment-distribution {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.fees-section {
  margin-top: 16px;
}

.card-actions {
  border-top: 1px solid #f0f0f0;
  padding-top: 12px;
  text-align: center;
}

.card-actions .ant-btn {
  border-radius: 20px;
  font-size: 12px;
  height: 32px;
  padding: 0 16px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .payment-cards-container {
    column-count: 1;
  }

  .payment-card {
    margin-bottom: 16px;
  }

  .fees-table {
    font-size: 12px;
  }
}

@keyframes pulse {
  0% {
    box-shadow: 0 4px 12px rgba(24, 144, 255, 0.15);
  }
  50% {
    box-shadow: 0 4px 20px rgba(24, 144, 255, 0.25);
  }
  100% {
    box-shadow: 0 4px 12px rgba(24, 144, 255, 0.15);
  }
}

/* Tag styles */
.ant-tag {
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  padding: 2px 8px;
}

/* Divider styles */
.ant-divider {
  margin: 16px 0;
  color: #666;
  font-weight: 500;
}

.ant-divider::before,
.ant-divider::after {
  border-top-color: #d9d9d9;
}

/* Clickable rows */
.clickable-row {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.clickable-row:hover {
  background-color: #f5f5f5;
}

.clickable-row.ant-table-row-selected {
  background-color: #e6f7ff;
}

/* Clickable amount styles */
.clickable-amount {
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 6px;
  border: 2px solid #ffcdd2;
  transition: all 0.3s ease;
  position: relative;
  background: #ffebee;
  margin: 0;
  min-height: 60px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.amount-with-remaining {
  background: #fff3cd !important;
  border-color: #ffeaa7 !important;
}

.amount-with-remaining .fee-amount {
  color: #856404 !important;
}

.amount-with-remaining .fee-final-amount {
  color: #6c5ce7 !important;
}

.clickable-amount:hover {
  background: #ffcdd2;
  border-color: #ef5350;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(244, 67, 54, 0.15);
}

.amount-with-remaining:hover {
  background: #ffeaa7 !important;
  border-color: #fdcb6e !important;
}

.amount-selected {
  background: #e6f7ff !important;
  border-color: #1890ff !important;
  box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15);
}

.amount-selected:hover {
  background: #bae7ff !important;
  border-color: #40a9ff !important;
}

.amount-selected .fee-amount {
  color: #1890ff !important;
}

.amount-selected .fee-final-amount {
  color: #52c41a !important;
}

.selection-indicator {
  position: absolute;
  top: 8px;
  right: 8px;
  font-size: 16px;
}

.fee-amount {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #d32f2f;
  margin-bottom: 4px;
  flex-wrap: wrap;
}

.fee-final-amount {
  font-size: 12px;
  color: #52c41a;
  font-weight: 500;
  margin-top: 4px;
}

.toggle-all-btn {
  margin-bottom: 16px;
}

.student-name-tag {
  font-size: 13px;
}

/* Daily Fees Grid Styles */
.daily-fees-container {
  margin-top: 16px;
}

.daily-fees-info {
  font-size: 12px;
  color: #666;
  font-weight: normal;
}

.daily-fees-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 8px;
  margin-bottom: 16px;
}

.daily-fee-item {
  background: #ffebee;
  border: 2px solid #ffcdd2;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  min-height: 100px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 0;
}

.daily-fee-item:hover {
  background: #ffcdd2;
  border-color: #ef5350;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(244, 67, 54, 0.15);
}

.daily-fee-item.amount-selected {
  background: #e6f7ff !important;
  border-color: #1890ff !important;
  box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15);
}

.daily-fee-item.amount-selected:hover {
  background: #bae7ff !important;
  border-color: #40a9ff !important;
}

.daily-fee-item.amount-with-remaining {
  background: #fff3cd !important;
  border-color: #ffeaa7 !important;
}

.daily-fee-item.amount-with-remaining:hover {
  background: #ffeaa7 !important;
  border-color: #fdcb6e !important;
}

.daily-fee-item.fee-paid {
  background: #d4edda !important;
  border-color: #c3e6cb !important;
  cursor: not-allowed;
  opacity: 0.7;
}

.fee-date {
  font-size: 12px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 4px;
  text-align: center;
}

.daily-fee-item .fee-amount {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #d32f2f;
  margin-bottom: 4px;
  flex-wrap: wrap;
  justify-content: center;
}

.daily-fee-item.amount-selected .fee-amount {
  color: #1890ff !important;
}

.daily-fee-item.amount-with-remaining .fee-amount {
  color: #856404 !important;
}

.daily-fee-item.fee-paid .fee-amount {
  color: #28a745 !important;
}

.daily-fee-item .fee-final-amount {
  font-size: 12px;
  color: #52c41a;
  font-weight: 500;
  margin-top: 4px;
  text-align: center;
}

.daily-fee-item.amount-selected .fee-final-amount {
  color: #52c41a !important;
}

.daily-fee-item .selection-indicator {
  position: absolute;
  top: 8px;
  right: 8px;
  font-size: 16px;
}

.text-success {
  color: #28a745 !important;
}

.text-warning {
  color: #ffc107 !important;
}

.text-primary {
  color: #1890ff !important;
}

.text-muted {
  color: #6c757d !important;
}

/* Daily Fees Pagination */
.daily-fees-pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.pagination-info {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

/* Responsive adjustments for daily fees */
@media (max-width: 768px) {
  .daily-fees-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 6px;
  }
  
  .daily-fee-item {
    padding: 8px 6px;
    min-height: 90px;
  }
  
  .fee-date {
    font-size: 11px;
  }
  
  .daily-fee-item .fee-amount {
    font-size: 12px;
  }
  
  .daily-fees-pagination {
    flex-direction: column;
    gap: 8px;
  }
}

@media (max-width: 480px) {
  .daily-fees-grid {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  }
  
  .daily-fee-item {
    padding: 6px 4px;
    min-height: 80px;
  }
  
  .daily-fee-item .fee-amount {
    font-size: 11px;
  }
  
  .daily-fee-item .fee-final-amount {
    font-size: 10px;
  }
}
</style>
